import { englishError } from "../utils/databaseErrors";
import { validMarbleQuantity } from "../utils/marbleQuantity";
// src/components/ReturnNoteModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  fetchSaleDetailsRemaining,
  createReturnNote,
  getReturnNoteById,
} from "./returns";
import logo from "../assets/PdfImagetrs.png";

export default function ReturnNoteModal({ open, onClose, sale }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !sale?.customer_id) return;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchSaleDetailsRemaining(sale.customer_id);
        const filtered = data.filter((r) => r.sale_id === sale.id);
        setRows(filtered);
        setSelected([]);
        setReason("");
      } catch (err) {
        alert(englishError(err.message) || "Failed to load remaining lines");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, sale]);

  function addRow(r) {
    setSelected((prev) => {
      if (prev.some((x) => x.sale_detail_id === r.sale_detail_id)) return prev;

      const areaSold = Number(r.area_sold || 0);
      const slabsSold = Number(r.slabs_sold || 0);
      const areaRem = Number(r.area_remaining || 0);
      const slabsRem = Number(r.slabs_remaining || 0);

      const avg =
        slabsSold > 0 && areaSold > 0
          ? areaSold / slabsSold
          : slabsRem > 0
          ? areaRem / slabsRem
          : 0;

      return [
        ...prev,
        {
          sale_detail_id: r.sale_detail_id,
          block_id: r.block_id,
          showroom_id: r.showroom_id,
          slabs_returned: 0,
          area_returned_m2: 0,
          price_per_m2: Number(r.price_per_m2) || 0,
          max_slabs: slabsRem || 0,
          max_area: areaRem || 0,
          avg_area_per_slab: avg,
        },
      ];
    });
  }

  function updateRow(i, key, value) {
    setSelected((prev) =>
      prev.map((x, idx) => (idx === i ? { ...x, [key]: value } : x))
    );
  }

  const totalRefund = useMemo(
    () =>
      selected.reduce(
        (s, x) =>
          s + Number(x.area_returned_m2 || 0) * Number(x.price_per_m2 || 0),
        0
      ),
    [selected]
  );

  async function saveReturn() {
    if (!selected.length) return alert("Add at least one line");

    for (const x of selected) {
      const slabs = Number(x.slabs_returned || 0);
      const area = Number(x.area_returned_m2 || 0);
      const p = Number(x.price_per_m2 || 0);
      if (!validMarbleQuantity(slabs,true) || area < 0 || p <= 0) return alert("Invalid quantity, area or price.");
      if (slabs > Number(x.max_slabs))
        return alert("Slab count exceeds available stock");
      if (area > Number(x.max_area))
        return alert("Returned area exceeds available area");
    }

    const items = selected.map(
      ({ max_slabs, max_area, avg_area_per_slab, ...rest }) => ({
        ...rest,
        slabs_returned: Number(rest.slabs_returned || 0),
        area_returned_m2: Number(rest.area_returned_m2 || 0),
        price_per_m2: Number(rest.price_per_m2 || 0),
      })
    );

    setSaving(true);
    try {
      const res = await createReturnNote({
        customer_id: sale.customer_id,
        original_sale_id: sale.id,
        reason,
        items,
      });

      if (!res || !res.return_id) {
        console.error("createReturnNote response:", res);
        alert("Unable to create return — Check permissions/values.");
        return;
      }

      alert(
        `Return created: ${
          res.return_id
        } — Total ${res.total_refund.toLocaleString()} EGP`
      );
      await printReturnInvoice(res.return_id);
      onClose?.(true);
    } catch (err) {
      console.error(err);
      alert(englishError(err.message) || "Unable to create return");
    } finally {
      setSaving(false);
    }
  }

  async function printReturnInvoice(returnId) {
    const data = await getReturnNoteById(returnId);
    const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Return invoice #${data.id} — For invoice ${
      data.original_sale_id
    }</title>
        <style>
          body { font-family: -apple-system, Segoe UI, Tahoma, Arial; direction: ltr; padding: 20px; }
          h2 { text-align: center; color: #514536; margin: 0 0 10px; }
          .print-logo { max-width: 150px; display: block; margin: 0 auto 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; }
          th, td { border: 1px solid #d2cbc1; padding: 6px 8px; text-align: center; }
          tfoot td { font-weight: bold; }
        </style>
      </head>
      <body>
        <img src="${logo}" alt="Logo" class="print-logo" />
        <h2>Return invoice (Credit Note)</h2>
        <div>Original invoice: <b>${data.original_sale_id}</b></div>
        <div>Date: ${new Date(data.date).toLocaleDateString()}</div>
        <div>Customer: ${data.customer?.name || ""} ${
      data.customer?.phone ? "— " + data.customer.phone : ""
    }</div>
        <div>Reason: ${data.reason || "-"}</div>
          <table>
            <thead>
              <tr>
                <th>Sale Detail</th>
                <th>Block</th>
                <th>Showroom</th>
                <th>Slabs Ret</th>
                <th>Area Ret (m²)</th>
                <th>Price/m²</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              ${(data.details || [])
                .map(
                  (d) => `
                <tr>
                  <td>${d.sale_detail_id}</td>
                  <td>${d.block_id}</td>
                  <td>${d.showroom_id}</td>
                  <td>${d.slabs_returned}</td>
                  <td>${Number(d.area_returned_m2).toFixed(3)}</td>
                  <td>${Number(d.price_per_m2).toFixed(2)}</td>
                  <td>${(
                    Number(d.area_returned_m2) * Number(d.price_per_m2)
                  ).toFixed(2)}</td>
                </tr>`
                )
                .join("")}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="6">Total</td>
                <td>${Number(data.total_refund).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
          <script>setTimeout(()=>{ window.print(); }, 250);</script>
        </body>
      </html>
    `;
    const win = window.open("", "_blank");
    if (win && win.document) {
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
    } else {
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.location.href = url;
    }
  }

  if (!open) return null;

  return (
    <div className="ui-backdrop" style={overlay}>
      <div className="ui-modal ui-modal-wide" style={modal}>
        <h3 style={{ marginTop: 0, color: "var(--accent, #514536)" }}>
          Create return — for invoice #{sale?.id}
        </h3>

        {/* السطور المتاحة */}
        <div className="ui-card" style={card}>
          <strong>Lines available to return</strong>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div className="ui-table-scroll"><table style={table}>
                <thead>
                  <tr>
                    <th>Detail</th>
                    <th>Block</th>
                    <th>Showroom</th>
                    <th>Slabs Rem</th>
                    <th>Area Rem (m²)</th>
                    <th>Price/m²</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.sale_detail_id}>
                      <td>{r.sale_detail_id}</td>
                      <td>{r.block_id}</td>
                      <td>{r.showroom_id}</td>
                      <td>{r.slabs_remaining}</td>
                      <td>{Number(r.area_remaining).toFixed(3)}</td>
                      <td>{Number(r.price_per_m2).toFixed(2)}</td>
                      <td><div className="ui-actions">
                        <button className="ui-secondary" onClick={() => addRow(r)}>Add</button>
                      </div></td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={7} style={{ color: "#716759" }}>
                        No lines available to return
                      </td>
                    </tr>
                  )}
                </tbody>
              </table></div>
            </div>
          )}
        </div>

        {/* تفاصيل المرتجع */}
        <div className="ui-card" style={card}>
          <strong>Return details</strong>
          <div style={{ overflowX: "auto" }}>
            <div className="ui-table-scroll"><table style={table}>
              <thead>
                <tr>
                  <th>Detail</th>
                  <th>Block</th>
                  <th>Slabs Return</th>
                  <th>Area Return m²</th>
                  <th>Price/m²</th>
                </tr>
              </thead>
              <tbody>
                {selected.map((x, i) => (
                  <tr key={i}>
                    <td>#{x.sale_detail_id}</td>
                    <td>{x.block_id}</td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        max={x.max_slabs}
                        step="0.001"
                        value={x.slabs_returned}
                        onChange={(e) => {
                          const slabs = Math.min(
                            Number(e.target.value || 0),
                            Number(x.max_slabs || 0)
                          );
                          const autoArea = Math.min(
                            Number(x.max_area || 0),
                            slabs * Number(x.avg_area_per_slab || 0)
                          );
                          updateRow(i, "slabs_returned", slabs);
                          updateRow(
                            i,
                            "area_returned_m2",
                            Number(autoArea.toFixed(3))
                          );
                        }}
                        style={{ width: 100 }}
                      />{" "}
                      <small>/ {x.max_slabs}</small>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.001"
                        min={0}
                        max={x.max_area}
                        value={x.area_returned_m2}
                        onChange={(e) =>
                          updateRow(
                            i,
                            "area_returned_m2",
                            Math.min(
                              Number(e.target.value || 0),
                              Number(x.max_area || 0)
                            )
                          )
                        }
                        style={{ width: 120 }}
                      />{" "}
                      <small>/ {Number(x.max_area).toFixed(3)}</small>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={x.price_per_m2}
                        onChange={(e) =>
                          updateRow(
                            i,
                            "price_per_m2",
                            parseFloat(e.target.value || 0)
                          )
                        }
                        style={{ width: 120 }}
                      />
                    </td>
                  </tr>
                ))}
                {!selected.length && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{ color: "#716759", textAlign: "center" }}
                    >
                      Add lines from the table above
                    </td>
                  </tr>
                )}
              </tbody>
            </table></div>
          </div>

          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Return reason"
            style={{ width: "100%", minHeight: 70, marginTop: 6 }}
          />

          <div style={{ marginTop: 10, fontWeight: "bold" }}>
            Total returns: {totalRefund.toLocaleString()} EGP
          </div>
        </div>

        <div className="ui-actions" style={{ display: "flex", gap: 10 }}>
          <button className="ui-primary"
            onClick={saveReturn}
            disabled={saving || !selected.length}
            style={{
              background: "var(--accent, #514536)",
              color: "#faf8f4",
              padding: "8px 20px",
              borderRadius: 8,
            }}
          >
            {saving ? "Saving..." : "Save and print return"}
          </button>
          <button className="ui-secondary"
            onClick={() => onClose?.(false)}
            style={{ background: "#d2cbc1", padding: "8px 18px", borderRadius: 8 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* Styles */
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(81,69,54,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 3000,
};
const modal = {
  background: "#faf8f4",
  padding: 18,
  borderRadius: 12,
  width: "min(980px, 96vw)",
  maxHeight: "92vh",
  overflowY: "auto",
  direction: "ltr",
  textAlign: "left",
};
const card = {
  border: "1px solid #d2cbc1",
  borderRadius: 8,
  padding: 10,
  marginBottom: 10,
};
const table = { width: "100%", borderCollapse: "collapse" };
